import React, { useState, useEffect, useRef } from 'react';
import { FaCloudUploadAlt, FaTrash, FaPlus, FaTimes } from 'react-icons/fa';
import { message, Switch } from 'antd';
import AdminService from '../services/adminService';

const UNIT_OPTIONS = [
  'None', 'pcs', 'Plate', 'Portion', 'Serving', 'Cup', 'Glass', 'Bowl', 'Slice',
  'g', 'kg', 'ml', 'l', 'pack', 'Packet', 'Bottle', 'Can', 'Box', 'Other'
];

const escapeRegex = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const cleanVariantBaseValue = (val, currentUnit) => {
  if (!val) return '';
  let str = String(val).trim();

  // 1. If currentUnit is provided and not 'none', strip it if at end
  if (currentUnit && currentUnit.trim() && currentUnit.toLowerCase() !== 'none') {
    const u = currentUnit.trim();
    const regex = new RegExp(`\\s+${escapeRegex(u)}$`, 'i');
    if (regex.test(str)) {
      const cand = str.replace(regex, '').trim();
      if (cand) str = cand;
    }
  }

  // 2. Check against UNIT_OPTIONS (skip single letters like 'l' unless length > 1)
  for (const unit of UNIT_OPTIONS) {
    if (unit && unit.toLowerCase() !== 'none' && unit.toLowerCase() !== 'other' && unit.length > 1) {
      const regex = new RegExp(`\\s+${escapeRegex(unit)}$`, 'i');
      if (regex.test(str)) {
        const cand = str.replace(regex, '').trim();
        if (cand) {
          str = cand;
          break;
        }
      }
    }
  }

  return str;
};

const formatVariantSizeLabel = (val, unit) => {
  const base = cleanVariantBaseValue(val, unit);
  const u = (unit || '').trim();
  if (base && u && u.toLowerCase() !== 'none') {
    return `${base} ${u}`;
  }
  return base || (u && u.toLowerCase() !== 'none' ? u : 'Regular');
};

const AddNewMenuItemModal = ({ onClose, onSave, businessId, businessData, isSaving }) => {
  const [formData, setFormData] = useState({
    item_name: '',
    sku: '',
    category: '',
    sub_category: '',
    item_type: 'Veg',
    preparation_time: '',
    size_label: '',
    description: '',
    quantity: '0',
    status: 'active',
    original_cost: '',
    gst: '2',
    selling_price: '',
    is_visible: true,
    is_featured: false,
    is_customizable: false,
    is_organic: false,
    is_visible_counter: true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [variants, setVariants] = useState([]);
  const [taxSlabs, setTaxSlabs] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [availabilityTimings, setAvailabilityTimings] = useState([]);
  const [useBusinessHours, setUseBusinessHours] = useState(true);
  const [businessHoursData, setBusinessHoursData] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const rawHours = businessData?.business_info?.business_hours || businessData?.business_hours;
    if (rawHours) {
      setBusinessHoursData(rawHours);
    } else if (businessId) {
      AdminService.getBusinessDetailedView(businessId)
        .then((res) => {
          const hours = res?.data?.business_info?.business_hours || res?.business_info?.business_hours || res?.data?.business_hours || res?.business_hours;
          if (hours) {
            setBusinessHoursData(hours);
          }
        })
        .catch((err) => {
          console.warn('Failed to load business hours:', err);
        });
    }
  }, [businessId, businessData]);

  useEffect(() => {
    const fetchTaxSlabs = async () => {
      try {
        const response = await AdminService.getTaxSlabs();
        if (response && response.success && response.data) {
          setTaxSlabs(response.data);
        } else if (Array.isArray(response)) {
          setTaxSlabs(response);
        }
      } catch (error) {
        console.error('Failed to load tax slabs:', error);
      }
    };
    fetchTaxSlabs();
  }, []);

  useEffect(() => {
    if (!businessId) return;
    const fetchCategories = async () => {
      try {
        const response = await AdminService.getBusinessCategories(businessId);
        if (response && response.success && response.data) {
          setApiCategories(response.data);
        } else if (Array.isArray(response)) {
          setApiCategories(response);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    fetchCategories();
  }, [businessId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'category') {
        newData.sub_category = ''; // reset sub category when category changes
      }
      return newData;
    });
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addVariant = () => {
    setVariants((prev) => [
      ...prev,
      {
        id: Date.now(),
        size_label: '',
        unit: '',
        pos_plu: '',
        original_cost: '',
        gst: '2',
        selling_price: '',
        stock_qty: '0',
        is_visible: true,
        is_visible_counter: true,
      },
    ]);
  };

  const removeVariant = (id) => {
    setVariants((prev) => prev.filter((v) => v.id !== id));
  };

  const handleVariantChange = (id, field, value) => {
    setVariants((prev) =>
      prev.map((v) => {
        if (v.id !== id) return v;
        if (field === 'unit') {
          const cleanedSizeLabel = cleanVariantBaseValue(v.size_label, v.unit);
          return { ...v, unit: value, size_label: cleanedSizeLabel || v.size_label };
        }
        return { ...v, [field]: value };
      })
    );
  };

  const addAvailabilityTiming = () => {
    setAvailabilityTimings((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), day: 'Mon', start: '09:00', end: '21:00' },
    ]);
  };

  const updateAvailabilityTiming = (id, field, value) => {
    setAvailabilityTimings((prev) =>
      prev.map((timing) =>
        timing.id === id ? { ...timing, [field]: value } : timing
      )
    );
  };

  const removeAvailabilityTiming = (id) => {
    setAvailabilityTimings((prev) => prev.filter((timing) => timing.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.item_name || !formData.original_cost || !formData.selling_price) {
      message.warning('Please fill out all required fields (*)');
      return;
    }

    if (Number(formData.original_cost) <= 0) {
      message.warning('Original Cost must be greater than 0');
      return;
    }

    if (Number(formData.selling_price) <= 0) {
      message.warning('Selling Price must be greater than 0');
      return;
    }

    if (formData.quantity === '' || Number(formData.quantity) <= 0) {
      message.warning('Stock Quantity must be greater than 0');
      return;
    }

    if (variants.length > 0) {
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        if (!v.original_cost || Number(v.original_cost) <= 0) {
          message.warning(`Variant ${i + 1}: Original Cost must be greater than 0`);
          return;
        }
        if (v.selling_price !== '' && Number(v.selling_price) <= 0) {
          message.warning(`Variant ${i + 1}: Selling Price must be greater than 0`);
          return;
        }
        if (v.stock_qty !== '' && Number(v.stock_qty) <= 0) {
          message.warning(`Variant ${i + 1}: Stock Quantity must be greater than 0`);
          return;
        }
      }
    }

    if (!formData.category) {
      message.warning('Please select a Category');
      return;
    }

    const selectedCatObj = apiCategories.find(c => (c.category_name || '').trim().toLowerCase() === (formData.category || '').trim().toLowerCase());
    const subCategories = selectedCatObj?.sub_categories || [];
    if (subCategories.length > 0 && !formData.sub_category) {
      message.warning('Please select a Sub Category');
      return;
    }

    const selectedSubCatObj = subCategories.find(sc => (sc.category_name || '').trim().toLowerCase() === (formData.sub_category || '').trim().toLowerCase());

    const payload = new FormData();
    payload.append('item_name', formData.item_name);
    payload.append('sku', formData.sku);
    payload.append('item_category', formData.category);
    payload.append('category_name', formData.category);
    if (selectedCatObj && selectedCatObj.category_id) {
      payload.append('category_id', String(selectedCatObj.category_id));
    }
    payload.append('sub_category', formData.sub_category);
    if (selectedSubCatObj && selectedSubCatObj.category_id) {
      payload.append('sub_category_id', String(selectedSubCatObj.category_id));
    }
    payload.append('item_type', formData.item_type);
    payload.append('preparation_time', formData.preparation_time);
    payload.append('size_label', formData.size_label);
    payload.append('description', formData.description);
    payload.append('status', formData.status === 'active' ? '1' : '0');
    payload.append('is_variable', '1');

    payload.append('is_featured', formData.is_featured ? '1' : '0');
    payload.append('is_customizable', formData.is_customizable ? '1' : '0');
    payload.append('is_organic', formData.is_organic ? '1' : '0');

    if (imageFile) {
      payload.append('item_image', imageFile);
    }

    const baseRawVal = cleanVariantBaseValue(formData.size_label, '');
    const baseCombined = baseRawVal || 'Regular';
    const baseSizeLabelObj = {
      unit: '',
      value: baseRawVal || baseCombined,
      size_label: baseCombined,
    };

    payload.append('size_label', JSON.stringify(baseSizeLabelObj));
    payload.append('size', JSON.stringify(baseSizeLabelObj));
    payload.append('value', baseRawVal || baseCombined);

    const baseVariant = {
      value: baseRawVal || baseCombined,
      size_label: baseSizeLabelObj,
      size: baseSizeLabelObj,
      unit: '',
      net_weight_unit: '',
      pos_plu: '',
      plu_code: '',
      plu: '',
      original_cost: formData.original_cost,
      gst: formData.gst,
      selling_price: formData.selling_price,
      stock_qty: formData.quantity,
      is_visible: true,
      is_visible_counter: true,
    };

    let finalVariants = [];
    if (variants.length > 0) {
      finalVariants = [
        baseVariant,
        ...variants.map(v => {
          const vUnit = (v.unit || '').trim();
          const vRawVal = cleanVariantBaseValue(v.value || v.size_label || formData.size_label, vUnit);
          const vCombined = formatVariantSizeLabel(vRawVal, vUnit);
          const vSizeLabelObj = {
            unit: vUnit,
            value: vRawVal || vCombined,
            size_label: vCombined,
          };
          return {
            value: vRawVal || vCombined,
            size_label: vSizeLabelObj,
            size: vSizeLabelObj,
            unit: vUnit,
            net_weight_unit: vUnit,
            pos_plu: v.pos_plu || '',
            plu_code: v.pos_plu || '',
            plu: v.pos_plu || '',
            original_cost: v.original_cost || formData.original_cost,
            gst: v.gst || formData.gst,
            selling_price: v.selling_price || formData.selling_price,
            stock_qty: v.stock_qty || formData.quantity,
            is_visible: v.is_visible !== false,
            is_visible_counter: v.is_visible_counter !== false,
          };
        })
      ];
    } else {
      finalVariants = [baseVariant];
    }

    payload.append('variants', JSON.stringify(finalVariants));

    payload.append('price', formData.selling_price);
    payload.append('original_cost', formData.original_cost);

    if (useBusinessHours) {
      if (businessHoursData) {
        payload.append('availability_timings', typeof businessHoursData === 'string' ? businessHoursData : JSON.stringify(businessHoursData));
      }
      payload.append('use_business_hours', '1');
    } else {
      if (availabilityTimings.length > 0) {
        const timingsObject = availabilityTimings.reduce((acc, timing) => {
          if (timing.day && timing.start && timing.end) {
            const dayLower = timing.day.toLowerCase();
            if (!acc[dayLower]) {
              acc[dayLower] = [];
            }
            acc[dayLower].push({ open: timing.start, close: timing.end });
          }
          return acc;
        }, {});
        if (Object.keys(timingsObject).length > 0) {
          payload.append('availability_timings', JSON.stringify(timingsObject));
        }
      }
      payload.append('use_business_hours', '0');
    }

    onSave(payload);
  };

  // Styles for the premium modal matching dashboard
  const overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(0px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '20px'
  };

  const modalStyle = {
    background: '#ffffff', color: '#111827', borderRadius: '8px',
    width: '100%', maxWidth: '750px', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    fontFamily: 'Inter, sans-serif'
  };

  const headerStyle = {
    padding: '20px', borderBottom: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  };

  const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' };

  const closeButtonStyle = {
    background: 'none', border: 'none', color: '#6b7280', fontSize: '24px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'color 0.2s', padding: 0
  };

  const bodyStyle = {
    padding: '20px', overflowY: 'auto', flex: 1,
    display: 'flex', flexDirection: 'column', gap: '20px'
  };

  const rowStyle = {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'
  };

  const inputGroupStyle = {
    display: 'flex', flexDirection: 'column', gap: '6px'
  };

  const labelStyle = {
    fontSize: '14px', color: '#374151', fontWeight: '500', textAlign: 'left', display: 'block'
  };

  const inputStyle = {
    background: '#ffffff', border: '1px solid #d1d5db', borderRadius: '4px',
    padding: '10px 12px', color: '#111827', fontSize: '14px', outline: 'none',
    width: '100%', boxSizing: 'border-box', transition: 'border-color 0.2s'
  };

  const subTextStyle = {
    fontSize: '11px', color: '#6b7280', marginTop: '-2px'
  };

  const uploadBoxStyle = {
    border: '1px dashed #d1d5db', borderRadius: '4px', padding: '20px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', background: '#f9fafb', color: '#3b82f6',
    gap: '8px', transition: 'background 0.2s'
  };

  const variantBoxStyle = {
    border: '1px dashed #d1d5db', borderRadius: '8px', padding: '30px',
    textAlign: 'center', color: '#6b7280', fontSize: '14px',
    background: '#f9fafb', display: 'flex', flexDirection: 'column', gap: '10px'
  };

  const variantCardStyle = {
    background: '#f9fafb', borderRadius: '8px', padding: '20px', border: '1px solid #e5e7eb',
    display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative'
  };

  const footerStyle = {
    padding: '20px', borderTop: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px'
  };

  const cancelBtnStyle = {
    background: '#f9fafb', border: '1px solid #d1d5db', color: '#374151',
    padding: '8px 16px', borderRadius: '6px', fontWeight: '500',
    cursor: 'pointer', fontSize: '14px', transition: 'background 0.2s'
  };

  const addBtnStyle = {
    background: '#3b82f6', border: 'none', color: '#ffffff',
    padding: '8px 16px', borderRadius: '6px', fontWeight: '500',
    cursor: 'pointer', fontSize: '14px', transition: 'background 0.2s'
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>Add New Menu Item</h2>
          <button style={closeButtonStyle} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div style={bodyStyle}>
          <div style={rowStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Item Name *</label>
              <input style={inputStyle} name="item_name" value={formData.item_name} onChange={handleChange} placeholder="e.g., Veg Burger" />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>SKU</label>
              <input style={inputStyle} name="sku" value={formData.sku} onChange={handleChange} placeholder="Leave empty for auto-generation" />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Category *</label>
              <select style={inputStyle} name="category" value={formData.category} onChange={handleChange}>
                <option value="" disabled>Select Category</option>
                {apiCategories.map((c, i) => <option key={i} value={c.category_name}>{c.category_name}</option>)}
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>
                Sub Category {(apiCategories.find(c => c.category_name === formData.category)?.sub_categories || []).length > 0 ? '*' : ''}
              </label>
              <select style={inputStyle} name="sub_category" value={formData.sub_category} onChange={handleChange}>
                <option value="">Select Sub Category</option>
                {apiCategories.find(c => c.category_name === formData.category)?.sub_categories?.map((sc, i) => (
                  <option key={i} value={sc.category_name}>{sc.category_name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={rowStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Item Type</label>
              <select style={inputStyle} name="item_type" value={formData.item_type} onChange={handleChange}>
                <option value="Veg">Veg</option>
                <option value="Non-Veg">Non-Veg</option>
                <option value="Egg">Egg</option>
                <option value="Vegan">Vegan</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Preparation Time</label>
              <input style={inputStyle} name="preparation_time" value={formData.preparation_time} onChange={handleChange} placeholder="e.g., 15 mins" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Size Label (e.g., R/S/L/D)</label>
              <input style={inputStyle} name="size_label" value={formData.size_label} onChange={handleChange} placeholder="e.g., R" />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Quantity *</label>
              <input style={inputStyle} name="quantity" type="number" min="1" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={formData.quantity} onChange={handleChange} />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Original Cost (₹) *</label>
              <input style={inputStyle} name="original_cost" type="number" min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={formData.original_cost} onChange={handleChange} placeholder="0" />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>GST (%)</label>
              <select style={inputStyle} name="gst" value={formData.gst} onChange={handleChange}>
                {taxSlabs.length > 0 ? (
                  taxSlabs.map(slab => (
                    <option key={slab.id} value={slab.id}>{slab.tax_name}</option>
                  ))
                ) : (
                  <>
                    <option value="1">0%</option>
                    <option value="2">5%</option>
                    <option value="3">12%</option>
                    <option value="4">18%</option>
                    <option value="5">28%</option>
                  </>
                )}
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Selling Price (₹) *</label>
              <input style={inputStyle} name="selling_price" type="number" min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={formData.selling_price} onChange={handleChange} placeholder="0" />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Description (Optional)</label>
              <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'none', height: '100%' }} name="description" value={formData.description} onChange={handleChange} placeholder="Enter description..." />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Item Image</label>
              <div style={{ ...uploadBoxStyle, height: '100%', minHeight: '80px', padding: '10px' }} onClick={handleImageClick}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" style={{ maxHeight: '80px', borderRadius: '4px' }} />
                ) : (
                  <>
                    <FaCloudUploadAlt size={24} />
                    <span style={{ fontWeight: '500' }}>Upload Image</span>
                  </>
                )}
              </div>
              <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
            </div>
          </div>

          <div style={{ width: '100%', padding: '16px 0', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', margin: '10px 0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '16px' }}>
                PRODUCT OPTIONS
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '30px', justifyContent: 'flex-start', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch checked={formData.is_featured} onChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))} size="small" />
                  <span style={{ fontSize: '14px', color: '#374151' }}>Featured</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch checked={formData.is_customizable} onChange={(checked) => setFormData(prev => ({ ...prev, is_customizable: checked }))} size="small" />
                  <span style={{ fontSize: '14px', color: '#374151' }}>Customizable</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch checked={formData.is_organic} onChange={(checked) => setFormData(prev => ({ ...prev, is_organic: checked }))} size="small" />
                  <span style={{ fontSize: '14px', color: '#374151' }}>Organic</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Switch checked={useBusinessHours} onChange={setUseBusinessHours} size="small" />
                  <span style={{ fontSize: '14px', color: '#374151' }}>Use Business Hours</span>
                </div>
              </div>
            </div>
          </div>

          {!useBusinessHours && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <label style={{ ...labelStyle, fontSize: '14px', color: '#111827', fontWeight: '600', margin: 0 }}>Availability Timings</label>
                <button type="button" onClick={addAvailabilityTiming} style={{ background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500' }}>
                  <FaPlus size={10} /> Add Timing
                </button>
              </div>

              {availabilityTimings.length === 0 ? (
                <div style={variantBoxStyle}>
                  <p style={{ margin: 0 }}>No availability timings added.</p>
                  <p style={{ margin: 0, fontSize: '12px' }}>Click "Add Timing" to configure item availability by day.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {availabilityTimings.map((timing) => (
                    <div key={timing.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '12px', alignItems: 'center' }}>
                      <select style={inputStyle} value={timing.day} onChange={(e) => updateAvailabilityTiming(timing.id, 'day', e.target.value)}>
                        <option value="Mon">Mon</option>
                        <option value="Tue">Tue</option>
                        <option value="Wed">Wed</option>
                        <option value="Thu">Thu</option>
                        <option value="Fri">Fri</option>
                        <option value="Sat">Sat</option>
                        <option value="Sun">Sun</option>
                      </select>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Open:</span>
                        <input type="time" style={{ ...inputStyle, flex: 1 }} value={timing.start} onChange={(e) => updateAvailabilityTiming(timing.id, 'start', e.target.value)} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Close:</span>
                        <input type="time" style={{ ...inputStyle, flex: 1 }} value={timing.end} onChange={(e) => updateAvailabilityTiming(timing.id, 'end', e.target.value)} />
                      </div>
                      <button type="button" onClick={() => removeAvailabilityTiming(timing.id)} style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: '6px', padding: '8px 12px', cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: '14px', color: '#111827', fontWeight: '600' }}>Item Variants</label>
              <button onClick={addVariant} style={{ background: 'transparent', border: '1px solid #3b82f6', color: '#3b82f6', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500' }}>
                <FaPlus size={10} /> Add Variant
              </button>
            </div>

            {variants.length === 0 ? (
              <div style={variantBoxStyle}>
                <p style={{ margin: 0 }}>No variants added yet</p>
                <p style={{ margin: 0, fontSize: '12px' }}>Click "Add Variant" to create size and price variations</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {variants.map((v, i) => (
                  <div key={v.id} style={variantCardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>Variant {i + 1}</span>
                      <button onClick={() => removeVariant(v.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                        <FaTrash size={14} />
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Size Label</label>
                        <input style={inputStyle} value={v.size_label} onChange={(e) => handleVariantChange(v.id, 'size_label', e.target.value)} placeholder="Size Label" />
                      </div>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Unit</label>
                        <select style={inputStyle} value={v.unit || ''} onChange={(e) => handleVariantChange(v.id, 'unit', e.target.value)}>
                          {UNIT_OPTIONS.map((u) => <option key={u} value={u === 'None' ? '' : u}>{u}</option>)}
                        </select>
                      </div>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Original Cost (₹) *</label>
                        <input style={inputStyle} type="number" min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={v.original_cost} onChange={(e) => handleVariantChange(v.id, 'original_cost', e.target.value)} placeholder="0.00" />
                      </div>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Selling Price (₹) *</label>
                        <input style={inputStyle} type="number" min="0.01" step="any" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={v.selling_price} onChange={(e) => handleVariantChange(v.id, 'selling_price', e.target.value)} placeholder="0.00" />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '12px', alignItems: 'flex-end' }}>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>Stock Quantity</label>
                        <input style={inputStyle} type="number" min="1" onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }} value={v.stock_qty} onChange={(e) => handleVariantChange(v.id, 'stock_qty', e.target.value)} placeholder="0" />
                      </div>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>GST (%)</label>
                        <select style={inputStyle} value={v.gst} onChange={(e) => handleVariantChange(v.id, 'gst', e.target.value)}>
                          {taxSlabs.length > 0 ? (
                            taxSlabs.map(slab => (
                              <option key={slab.id} value={slab.id}>{slab.tax_name}</option>
                            ))
                          ) : (
                            <>
                              <option value="1">0%</option>
                              <option value="2">5%</option>
                              <option value="3">12%</option>
                              <option value="4">18%</option>
                              <option value="5">28%</option>
                            </>
                          )}
                        </select>
                      </div>
                      <div style={inputGroupStyle}>
                        <label style={labelStyle}>POS PLU</label>
                        <input style={inputStyle} value={v.pos_plu || ''} onChange={(e) => handleVariantChange(v.id, 'pos_plu', e.target.value)} placeholder="Optional PLU code" />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', paddingBottom: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <Switch size="small" checked={v.is_visible !== false} onChange={(checked) => handleVariantChange(v.id, 'is_visible', checked)} />
                          Visible
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <Switch size="small" checked={v.is_visible_counter !== false} onChange={(checked) => handleVariantChange(v.id, 'is_visible_counter', checked)} />
                          Visible Counter
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={footerStyle}>
          <button style={cancelBtnStyle} onClick={onClose}>Cancel</button>
          <button style={{ ...addBtnStyle, background: isSaving ? '#9ca3af' : '#3b82f6' }} onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Adding...' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNewMenuItemModal;
